/**
 * Вкладка «Звонки» экрана `/calls`: все звонки своей копии `bitrix_call` с
 * фильтрами; звонок — две строки (поля, под ними текст разговора), клик —
 * карточка `/calls/<id>`; хронология лида/сделки — панелью справа.
 * Спеки `docs/specs/2026-09-26-calls-tabs-design.md`, `docs/specs/2026-09-26-calls-list-v3-design.md`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ExternalLink, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { CallTags } from "@/components/CallTags";
import { Combo } from "@/components/Combo";
import { DateRangePicker } from "@/components/DateRangePicker";
import { bitrixUrl, type CallItem, type CallWord, type ChangeSummary, type ListParams, loadCallsList, loadCallWords, loadObjectCalls, type ObjectKind, type OutcomeChange } from "@/lib/calls";
import { shortTime } from "@/lib/leads";

export const TYPE: Record<number, string> = { 1: "исходящий", 2: "входящий", 3: "входящий (переадр.)", 4: "callback", 5: "авто" };
const KINDS = [
  { value: "", label: "все" },
  { value: "in", label: "входящие" },
  { value: "out", label: "исходящие" },
];
const TALK: Record<string, string> = { talk: "разговор", none: "не разговор", unclear: "не понять" };
/** Фильтр по разметке модели `bitrix_call_talk`; «не размечен» — соединились, модель ещё не читала. */
const TALKS = [
  { value: "", label: "все" },
  { value: "talk", label: "разговор" },
  { value: "none", label: "не разговор" },
  { value: "unclear", label: "не понять" },
  { value: "pending", label: "не размечен" },
];
/** Фильтр итога: общие варианты; стадии «куда перевели» приходят в `changes.to`. */
const CHANGES: { value: keyof Omit<ChangeSummary, "to"> | ""; label: string }[] = [
  { value: "", label: "все" },
  { value: "none", label: "без правок" },
  { value: "any", label: "есть правки" },
  { value: "stage", label: "стадия менялась" },
  { value: "nostage", label: "стадия не менялась" },
];
/** Подписи выборки из «Активности»: какие исходящие и какой шаг. */
const ACT_KIND: Record<string, string> = {
  out: "исходящие",
  lead: "исходящие по лидам",
  lead_work: "исходящие по лидам в работе",
  lead_new: "исходящие по новым лидам",
  deal: "исходящие по сделкам",
  rest: "прочие исходящие",
};
const ACT_STEP: Record<string, string> = {
  talk: "дозвон — был разговор",
  next: "дальше по воронке — записали или сделка",
  zapis: "после звонка записали",
  deal: "лид в сделку без записи",
  work: "лид — в работу",
  refuse: "лид — отказ",
};
/** Фильтр итога считает весь период — сервер берёт не больше недели. */
const CHANGE_MAX_DAYS = 7;

const today = () => new Date().toLocaleDateString("sv-SE");

/** Фильтры, страница и открытая панель — в адресе: ссылку можно переслать. */
function useListParams() {
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || today();
  const p = {
    from,
    to: params.get("to") || from,
    type: params.get("type") || "",
    user: params.get("user") || "",
    missed: params.get("missed") || "",
    talk: params.get("talk") || "",
    change: params.get("change") || "",
    // Звонки по сделкам когорты дня обзвона — переход из «Дней» (`/dialing?tab=days`).
    dialing: params.get("dialing") || "",
    dtype: params.get("dtype") || "",
    tag: params.get("tag") || "",
    // Разговоры, после которых записали, — переход с числа в «Записях» (`/appointments`).
    zapis: params.get("zapis") || "",
    zuser: params.get("zuser") || "",
    // Исходящие сотрудника — переход с числа в «Активности» (`/activity`).
    act: params.get("act") || "",
    auser: params.get("auser") || "",
    astep: params.get("astep") || "",
    // Слово в разговоре: ищем целиком по расшифровкам (задача владельца 14.10.26).
    q: params.get("q") || "",
    page: params.get("page") || "",
    panel: params.get("panel") || "",
  };
  const set = (next: Partial<typeof p>) => {
    // Любой фильтр возвращает на первую страницу.
    const v = { ...p, page: "", ...next };
    const out = new URLSearchParams({ tab: "list" });
    for (const [k, val] of Object.entries(v)) if (val) out.set(k, val);
    if (out.get("to") === out.get("from")) out.delete("to");
    setParams(out, { replace: true });
  };
  return [p, set] as const;
}

/** Исход звонка: соединились (и был ли разговор по расшифровке) или без ответа с причиной Bitrix. */
export function Outcome({ c }: { c: CallItem }) {
  if (!c.answered)
    return (
      <span className="text-warn" title={`${c.code} ${c.reason}`}>
        без ответа
      </span>
    );
  return (
    <span className="text-ok">{c.duration} с</span>
  );
}

/** Разговор по разметке модели: был ли живой разговор с клиентом. */
export function Talk({ c }: { c: CallItem }) {
  if (!c.answered) return <span className="text-fg-faint">—</span>;
  if (!c.talk) return <span className="text-fg-faint" title="модель ещё не размечала звонок">не размечен</span>;
  const tone = c.talk === "talk" ? "text-ok" : c.talk === "none" ? "text-fg-muted" : "text-warn";
  return <span className={tone}>{TALK[c.talk] ?? c.talk}</span>;
}

/** Правка итога одной строкой: «стадия: В работе → Думают». */
export function changeText(ch: OutcomeChange): string {
  const who = ch.object === "lead" ? "лид" : "сделка";
  if (ch.field === "created") return `${who} ${ch.label}`;
  return `${ch.label}: ${ch.old} → ${ch.new}`;
}

/** Итог звонка строкой над расшифровкой: что тот же сотрудник поменял в сделке/лиде
 *  в окне звонка. Главные поля — впереди и ярче, остальные — приглушённо. */
function OutcomeLine({ c }: { c: CallItem }) {
  if (!c.outcome) return null;
  const all = c.outcome.changes;
  const head = <span className="text-fg-faint">Итог: </span>;
  if (all.length === 0)
    return (
      <div title="в окне звонка сотрудник не менял сделку и лид">
        {head}
        <span className="text-fg-faint">без правок</span>
      </div>
    );
  const sorted = [...all.filter((ch) => ch.main), ...all.filter((ch) => !ch.main)];
  return (
    <div>
      {head}
      {sorted.map((ch, i) => (
        <span key={`${ch.object}-${ch.field}`} className={ch.main ? "text-fg" : "text-fg-muted"}>
          {i > 0 && <span className="text-fg-faint"> · </span>}
          {ch.field === "created" ? (
            <span className="text-accent-text">{changeText(ch)}</span>
          ) : (
            <>
              <span className="text-fg-muted">{ch.label} → </span>
              {ch.new}
            </>
          )}
        </span>
      ))}
    </div>
  );
}

/** Лид/сделка звонка: клик — панель справа с хронологией, значок — Bitrix. */
export function ObjectLink({
  kind,
  id,
  status,
  onOpen,
}: {
  kind: ObjectKind;
  id: number | null;
  status?: string | null;
  onOpen: (kind: ObjectKind, id: number) => void;
}) {
  if (!id) return <span className="text-fg-faint">—</span>;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(kind, id);
        }}
        className="font-mono tabular-nums underline decoration-border underline-offset-2 hover:decoration-fg"
        title={kind === "deal" ? "Звонки по сделке" : "Звонки по лиду"}
      >
        {id}
      </button>
      <a
        href={bitrixUrl(kind, id)}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="text-fg-faint hover:text-accent"
        title={kind === "deal" ? "Сделка в Bitrix" : "Лид в Bitrix"}
      >
        <ExternalLink size={11} aria-hidden />
      </a>
      {status && (
        <span className="text-[11.5px] text-fg-muted" title={kind === "deal" ? "Стадия сделки сейчас" : "Статус лида сейчас"}>
          {status}
        </span>
      )}
    </span>
  );
}

const COLUMNS = ["Время", "Тип", "Номер", "Сотрудник", "Исход", "Разговор", "Лид", "Сделка"];

/** Слова поиска целиком — та же регулярка, что на сервере (`kc.keywords.шаблон`). */
function wordsRx(q: string): RegExp | null {
  const parts = q
    .split(",")
    .map((w) => w.trim())
    .filter(Boolean)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return parts.length ? new RegExp(`(?<![\\p{L}\\p{N}])(${parts.join("|")})(?![\\p{L}\\p{N}])`, "giu") : null;
}

/** Расшифровка с подсветкой найденных слов: искали — видно, где именно звучало. */
function Transcript({ text, q }: { text: string; q: string }) {
  const rx = q ? wordsRx(q) : null;
  if (!rx) return <>{text}</>;
  const out: (string | React.ReactElement)[] = [];
  let last = 0;
  for (const m of text.matchAll(rx)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <mark key={`${at}`} className="rounded-[3px] bg-accent/25 px-0.5 text-fg">
        {m[0]}
      </mark>,
    );
    last = at + m[0].length;
  }
  out.push(text.slice(last));
  return <>{out}</>;
}

/** Звонок — две строки: поля по колонкам и под ними на всю ширину итог звонка и
 *  текст разговора. Столбцы — по содержимому, лишнюю ширину забирает пустой
 *  последний (владелец 26.09.26): иначе длинный текст растягивал их все.
 *  Клик по звонку — карточка `/calls/<id>`, её адрес можно переслать. */
function CallRows({
  calls,
  onOpen,
  showDate,
  onTag,
  q = "",
}: {
  calls: CallItem[];
  onOpen: (kind: ObjectKind, id: number) => void;
  showDate: boolean;
  /** Что искали в разговорах — подсвечиваем в тексте. */
  q?: string;
  /** Клик по тегу — фильтр списка по нему. */
  onTag?: (tag: string) => void;
}) {
  const navigate = useNavigate();
  // Заголовок звонка — на тон темнее фона, текст разговора под ним — на фоне таблицы:
  // иначе строка полей сливается с перепиской.
  const td = "bg-[color-mix(in_oklch,var(--fg)_12%,var(--bg))] px-2.5 py-2 font-medium text-fg group-hover:bg-[color-mix(in_oklch,var(--fg)_17%,var(--bg))]";
  return (
    <table className="w-full text-[12.5px]">
      <thead className="text-left text-[11.5px] text-fg-faint">
        <tr>
          {COLUMNS.map((c) => (
            <th key={c} className="whitespace-nowrap px-2.5 py-2 font-medium first:pl-4">
              {c}
            </th>
          ))}
          <th className="w-full" aria-hidden />
        </tr>
      </thead>
      {calls.map((c) => (
        <tbody
          key={c.id}
          className="group cursor-pointer border-t-[3px] border-border"
          onClick={(e) => {
            // Выделяют текст разговора — не уводим со страницы.
            if (!window.getSelection()?.isCollapsed) return;
            if (e.metaKey || e.ctrlKey) window.open(`/calls/${c.id}`, "_blank", "noopener");
            else navigate(`/calls/${c.id}`);
          }}
          data-annot={`звонок ${c.id}`}
        >
          <tr>
            <td className={`${td} whitespace-nowrap pl-4 tabular-nums`}>
              <Link to={`/calls/${c.id}`} onClick={(e) => e.stopPropagation()} className="underline decoration-border underline-offset-2 hover:decoration-fg" title="Карточка звонка">
                {showDate ? shortTime(c.at) : c.at.slice(11, 16)}
              </Link>
            </td>
            <td className={`${td} whitespace-nowrap`}>{TYPE[c.type_id] ?? `тип ${c.type_id}`}</td>
            <td className={`${td} whitespace-nowrap font-mono tabular-nums`}>
              {c.phone ?? "—"}
              {/* Пометка линии — под номером, а не рядом: иначе растягивает столбец. */}
              {c.line_note && <div className="font-sans text-[11px] font-normal text-fg-muted">{c.line_note}</div>}
            </td>
            <td className={`${td} whitespace-nowrap`}>{c.user ?? <span className="text-fg-faint">—</span>}</td>
            <td className={`${td} whitespace-nowrap tabular-nums`}>
              <Outcome c={c} />
            </td>
            <td className={`${td} whitespace-nowrap`}>
              <Talk c={c} />
            </td>
            <td className={td}>
              <ObjectLink kind="lead" id={c.lead_id} status={c.lead_status} onOpen={onOpen} />
            </td>
            <td className={td}>
              <ObjectLink kind="deal" id={c.deal_id} status={c.deal_status} onOpen={onOpen} />
            </td>
            <td className={td} aria-hidden />
          </tr>
          <tr>
            <td colSpan={COLUMNS.length + 1} className="bg-bg pb-3 pl-6 pr-4 pt-2 text-[12px] leading-[1.5]" data-annot={`итог и текст звонка ${c.id}`}>
              {/* w-0 + min-w-full: длинный текст не участвует в ширине столбцов. */}
              <div className="w-0 min-w-full">
                {/* Прослушка и теги прямо в списке: клики по ним не открывают карточку. */}
                <div className="mb-1.5 flex flex-wrap items-center gap-3">
                  {c.record && <audio controls preload="none" src={`/api/calls/${c.id}/record`} onClick={(e) => e.stopPropagation()} className="h-7 w-72 max-w-full" data-annot={`запись звонка ${c.id}`} />}
                  <CallTags key={`${c.id}:${(c.tags ?? []).join("|")}`} id={c.id} tags={c.tags ?? []} onFilter={onTag} />
                </div>
                <OutcomeLine c={c} />
                {c.transcript && (
                  <div className={`whitespace-pre-wrap border-l-2 border-border pl-3 font-normal text-fg-faint ${c.outcome ? "mt-1.5" : ""}`}>
                    <Transcript text={c.transcript} q={q} />
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      ))}
    </table>
  );
}

/** Панель справа: хронология звонков лида или сделки, старые сверху. */
export function ObjectPanel({ kind, id, onClose, onOpen }: { kind: ObjectKind; id: number; onClose: () => void; onOpen: (kind: ObjectKind, id: number) => void }) {
  const data = useQuery({ queryKey: ["calls-object", kind, id], queryFn: () => loadObjectCalls(kind, id) });
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const title = kind === "deal" ? `Сделка ${id}` : `Лид ${id}`;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(56rem,100vw)] flex-col border-l border-border bg-bg shadow-xl"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        role="dialog"
        aria-label={`Звонки: ${title}`}
        data-annot={`панель ${kind} ${id}`}
      >
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <h2 className="text-[14px] font-semibold">Звонки · {title}</h2>
          <Link to={kind === "deal" ? `/deals/${id}` : `/leads/${id}`} className="text-[12.5px] underline" title="Карточка с полной лентой">
            карточка
          </Link>
          <a href={bitrixUrl(kind, id)} target="_blank" rel="noreferrer noopener" className="text-[12.5px] text-fg-muted underline">
            в Bitrix
          </a>
          <button type="button" onClick={onClose} className="ml-auto rounded-[var(--radius-ui)] p-1 hover:bg-bg-hover" title="Закрыть (Esc)">
            <X size={16} aria-hidden />
            <span className="sr-only">Закрыть</span>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto py-2">
          {data.isPending ? (
            <div className="px-4 text-[12.5px] text-fg-muted">Собираю звонки…</div>
          ) : data.isError ? (
            <div className="px-4 text-[12.5px] text-err">Не удалось получить звонки: {String(data.error.message)}</div>
          ) : data.data.calls.length === 0 ? (
            <div className="px-4 text-[12.5px] text-fg-muted">Звонков нет.</div>
          ) : (
            <>
              <div className="px-4 pb-2 text-[11.5px] text-fg-faint">
                {data.data.calls.length} звонков · номера {data.data.phones.join(", ") || "—"} · старые сверху; клик по звонку —
                карточка
                {data.data.calls.length >= data.data.limit && ` · показаны первые ${data.data.limit}`}
              </div>
              <CallRows calls={data.data.calls} onOpen={onOpen} showDate />
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * «Что звучало в разговорах» — слова и пары слов расшифровок текущей выборки; клик по слову
 * ищет его в разговорах. Задача владельца 14.10.26: «сделать по выборке детализацию по ключевым
 * словам — какие слова использовались, и поиск текстом по ним».
 */
function Words({ req, q, onPick }: { req: Omit<ListParams, "page">; q: string; onPick: (word: string) => void }) {
  const [open, setOpen] = useState(false);
  // Слова считаем по выборке БЕЗ фильтра по слову: иначе число на чипе («прокат 18» внутри
  // найденных по «платье») не совпадает с тем, что откроется по клику (21 звонок).
  const без_слова = { ...req, q: "" };
  const данные = useQuery({
    queryKey: ["calls-words", без_слова],
    queryFn: () =>
      loadCallWords({
        from: req.from,
        to: req.to,
        type: req.type ?? "",
        user: req.user ?? "",
        missed: req.missed ?? "",
        talk: req.talk ?? "",
        tag: req.tag ?? "",
      }),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  const d = данные.data;
  const chip = (w: CallWord) => (
    <button
      key={w.word}
      type="button"
      onClick={() => onPick(w.word)}
      aria-pressed={q === w.word}
      title={`звучало в ${w.calls} разговорах выборки, всего ${w.hits} раз — показать эти звонки`}
      className={`rounded-[var(--radius-ui)] border px-2 py-0.5 text-[12px] hover:bg-bg-hover ${
        q === w.word ? "border-fg bg-bg text-fg" : "border-border text-fg-muted"
      }`}
    >
      {w.word} <span className="tabular-nums text-fg-faint">{w.calls}</span>
    </button>
  );
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="слова разговоров">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5">
        <button type="button" onClick={() => setOpen(!open)} className="text-[13px] font-medium hover:underline" aria-expanded={open}>
          {open ? "▾" : "▸"} Что звучало в разговорах
        </button>
        <span className="text-[11.5px] text-fg-faint">
          {open
            ? d
              ? `разговоров в выборке ${d.talks}${d.capped ? ` (считаем по последним ${d.limit})` : ""} · число у слова — в скольких они звучали · клик — эти звонки`
              : данные.isError
                ? String(данные.error.message)
                : "считаю…"
            : "слова и пары слов расшифровок этой выборки — сам фильтр по слову на них не влияет"}
        </span>
      </div>
      {open && d && (d.words.length > 0 || d.pairs.length > 0) && (
        <div className="space-y-2 border-t border-border px-4 py-3">
          <div className="flex flex-wrap gap-1.5" data-annot="слова">{d.words.map(chip)}</div>
          {d.pairs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-border pt-2" data-annot="пары слов">
              {d.pairs.map(chip)}
            </div>
          )}
        </div>
      )}
      {open && d && d.words.length === 0 && d.pairs.length === 0 && (
        <div className="border-t border-border px-4 py-3 text-[12.5px] text-fg-muted">
          Расшифровок в этой выборке нет — записи ещё не разобраны или звонки без разговора.
        </div>
      )}
    </section>
  );
}

export function CallsListTab() {
  const [p, set] = useListParams();
  // Поле ввода держим отдельно: запрос уходит по Enter или кнопке, а не на каждую букву.
  const [слово, setСлово] = useState(p.q);
  useEffect(() => setСлово(p.q), [p.q]);
  const req: ListParams = { from: p.from, to: p.to, type: p.type, user: p.user, missed: p.missed, talk: p.talk, change: p.change, dialing: p.dialing, dtype: p.dtype, tag: p.tag, zapis: p.zapis, zuser: p.zuser, act: p.act, auser: p.auser, astep: p.astep, q: p.q, page: p.page };
  const list = useQuery({ queryKey: ["calls-list", req], queryFn: () => loadCallsList(req), placeholderData: keepPreviousData });
  const page = Number(p.page || 0);
  const [panelKind, panelId] = p.panel.split(":") as [ObjectKind | "", string | undefined];
  const openPanel = (kind: ObjectKind, id: number) => set({ panel: `${kind}:${id}`, page: p.page });
  const userOptions = [
    { value: "", label: "все" },
    ...(list.data?.users ?? []).map((u) => ({ value: String(u.id), label: u.name, meta: String(u.calls) })),
  ];
  const tagOptions = [
    { value: "", label: "все" },
    ...(list.data?.tags ?? []).map((t) => ({ value: t.name, label: t.name, meta: String(t.calls) })),
    // Тег из ссылки, которого ещё нет в списке, — чтобы поле его показало.
    ...(p.tag && !(list.data?.tags ?? []).some((t) => t.name === p.tag) ? [{ value: p.tag, label: p.tag }] : []),
  ];
  const days = Math.round((Date.parse(p.to) - Date.parse(p.from)) / 86_400_000) + 1;
  const summary = list.data?.changes ?? null;
  // Стадии «куда перевели» известны, только когда фильтр итога уже включён: итог
  // всего периода считается лишь под него.
  const changeOptions = [
    ...CHANGES.map((c) => (summary && c.value ? { ...c, meta: String(summary[c.value]) } : c)),
    ...(summary?.to ?? []).map((t) => {
      const [obj, ...name] = t.value.split(":");
      return { value: `to:${t.value}`, label: `${obj === "lead" ? "лид" : "сделка"} → ${name.join(":")}`, meta: String(t.calls) };
    }),
  ];

  return (
    <>
      <div className="flex flex-col gap-2 text-[12.5px] text-fg-muted" data-annot="фильтры звонков">
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker label="Период" from={p.from} to={p.to} onChange={(from, to) => set({ from, to })} />
          <Combo label="Тип" value={p.type} options={KINDS} onChange={(type) => set({ type })} width={170} />
          <Combo label="Сотрудник" value={p.user} options={userOptions} onChange={(user) => set({ user })} placeholder="имя сотрудника" width={240} />
          <span title="Разметка модели: был ли живой разговор с клиентом">
            <Combo label="Разговор" value={p.talk} options={TALKS} onChange={(talk) => set({ talk })} width={180} />
          </span>
          <span title="Свои метки для разбора и прослушки; ставятся под звонком">
            <Combo label="Тег" value={p.tag} options={tagOptions} onChange={(tag) => set({ tag })} placeholder="тег" width={220} />
          </span>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!p.missed} onChange={(e) => set({ missed: e.target.checked ? "1" : "" })} />
            только без ответа
          </label>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              set({ q: слово.trim() });
            }}
            className="flex items-center gap-1.5"
            data-annot="поиск слова в разговоре"
          >
            <label className="flex items-center gap-1.5" title="Слово целиком в расшифровке разговора; несколько — через запятую">
              Слово в разговоре
              <input
                value={слово}
                onChange={(e) => setСлово(e.target.value)}
                placeholder="например вб, вайлдберриз"
                className="h-8 w-56 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg placeholder:text-fg-faint"
              />
            </label>
            <button type="submit" className="h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] hover:bg-bg-hover">
              найти
            </button>
            {p.q && (
              <button
                type="button"
                onClick={() => {
                  setСлово("");
                  set({ q: "" });
                }}
                className="text-accent hover:underline"
              >
                убрать «{p.q}»
              </button>
            )}
          </form>
        </div>
        {p.dialing && (
          <div className="flex flex-wrap items-center gap-2" data-annot="фильтр дня обзвона">
            <span className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-fg">
              только сделки обзвона за {p.dialing.slice(8, 10)}.{p.dialing.slice(5, 7)}
              {list.data?.dialing?.type && ` · ${list.data.dialing.type}`}
            </span>
            <Link to={`/dialing/day/${p.dialing}${p.dtype ? `?type=${p.dtype}` : ""}`} className="text-accent hover:underline">
              карточка дня
            </Link>
            <button type="button" onClick={() => set({ dialing: "", dtype: "" })} className="text-accent hover:underline">
              убрать
            </button>
          </div>
        )}
        {p.zapis && (
          <div className="flex flex-wrap items-center gap-2" data-annot="выборка по записям">
            <span className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-fg">
              только разговоры, после которых записали на примерку
              {list.data?.zapis && ` · ${list.data.zapis.user_name || "все сотрудники"} · ${list.data.zapis.label} · записано ${list.data.zapis.people}`}
            </span>
            <span className="text-[11.5px] text-fg-faint">период — дни записи</span>
            <Link to={`/appointments?from=${p.from}&to=${p.to}`} className="text-accent hover:underline">
              к записям
            </Link>
            <button type="button" onClick={() => set({ zapis: "", zuser: "" })} className="text-accent hover:underline">
              убрать
            </button>
          </div>
        )}
        {p.act && (
          <div className="flex flex-wrap items-center gap-2" data-annot="выборка из активности">
            <span className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-fg">
              {ACT_KIND[p.act] ?? "исходящие"}
              {p.astep && ` · ${ACT_STEP[p.astep] ?? p.astep}`}
              {list.data?.activity && ` · ${list.data.activity.user_name ?? "все сотрудники"} · звонков ${list.data.activity.calls}`}
            </span>
            {p.auser ? (
              <Link
                to={`/activity?from=${p.from}${p.to !== p.from ? `&to=${p.to}` : ""}&user=${p.auser}`}
                className="text-accent hover:underline"
              >
                к активности
              </Link>
            ) : (
              <Link to={`/calls?tab=days&from=${p.from}&to=${p.to}`} className="text-accent hover:underline">
                к дням
              </Link>
            )}
            <button type="button" onClick={() => set({ act: "", auser: "", astep: "" })} className="text-accent hover:underline">
              убрать
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3" data-annot="фильтр итога звонка">
          <Combo label="Итог звонка" value={p.change} options={changeOptions} onChange={(change) => set({ change })} placeholder="стадия" width={300} />
          <span className="text-[11.5px] text-fg-faint">
            {days > CHANGE_MAX_DAYS
              ? `итог считается за период до ${CHANGE_MAX_DAYS} дней — сузьте период`
              : summary
                ? "стадии — куда звонок перевёл сделку или лид; «без правок» — сотрудник ничего не поменял"
                : "выберите вариант — появятся стадии, куда переводили"}
          </span>
        </div>
      </div>
      <Words req={req} q={p.q} onPick={(word) => set({ q: word === p.q ? "" : word })} />
      {list.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Загружаю звонки…</div>
      ) : list.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить звонки: {String(list.error.message)}</div>
      ) : (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="список звонков">
          <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 text-[12.5px]">
            <h2 className="text-[13px] font-medium">
              Звонков: {list.data.total}
              {list.data.total > list.data.page_size && (
                <span className="font-normal text-fg-muted">
                  {" "}
                  · {page * list.data.page_size + 1}–{Math.min((page + 1) * list.data.page_size, list.data.total)}
                </span>
              )}
            </h2>
            <span className="text-fg-faint">новые сверху · клик по звонку — карточка · по лиду/сделке — все звонки клиента</span>
            <div className="ml-auto flex gap-1">
              <button type="button" disabled={page === 0} onClick={() => set({ page: page > 1 ? String(page - 1) : "", panel: p.panel })} className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] enabled:hover:bg-bg-hover disabled:opacity-40">
                ← новее
              </button>
              <button type="button" disabled={(page + 1) * list.data.page_size >= list.data.total} onClick={() => set({ page: String(page + 1), panel: p.panel })} className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] enabled:hover:bg-bg-hover disabled:opacity-40">
                старее →
              </button>
            </div>
          </div>
          {list.data.calls.length === 0 ? (
            <div className="p-4 text-[12.5px] text-fg-muted">Звонков нет.</div>
          ) : (
            <div className="overflow-x-auto">
              <CallRows calls={list.data.calls} onOpen={openPanel} showDate={list.data.from !== list.data.to} onTag={(tag) => set({ tag })} q={p.q} />
            </div>
          )}
        </section>
      )}
      {panelKind && panelId && Number(panelId) > 0 && (
        <ObjectPanel kind={panelKind} id={Number(panelId)} onClose={() => set({ panel: "", page: p.page })} onOpen={openPanel} />
      )}
    </>
  );
}
